<template>
	<v-row :key="key" no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmAttachments" />

		<v-col cols="12" class="pa-1" style="height: 60%">
			<v-card style="height: 100%; min-height: 300px" :loading="dataLoading">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('ccm-attachments-header') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<v-icon
								color="primary"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
								@click="synchronizeAddDrawers('ccmAttachmentAdd')">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-data-table
						:items="attachments"
						:headers="attachmentsHeaders"
						:items-per-page="-1"
						style="overflow-y: auto; height: 100%"
						:height="'calc(100% - 59px)'"
						fixed-header
						dense
						sort-by="timestamp"
						sort-desc
						class="data-table-outline"
						:footer-props="{ 'items-per-page-text': '' }">
						<template #item="{ item, index }">
							<tr :class="getRowsForDataTable(index)" @click.stop="openViewDialog(item, 'ccmAttachment')">
								<td class="text-left px-1">
									{{ item.documentType }}
								</td>
								<td class="text-left px-1 truncate-text" style="max-width: 20vw">
									<v-tooltip bottom>
										<template #activator="{ on }">
											<v-icon v-on="on" :size="$vuetify.breakpoint.mdAndDown ? 19 : 21" color="black">
												{{ getFileIcon(item.fileName) }}
											</v-icon>
										</template>
										{{ item.fileExtension.toUpperCase() }}
									</v-tooltip>
									{{ item.fileName }}
								</td>
								<td class="text-left px-1">
									{{ item.uploadedBy }}
								</td>
								<td class="text-left px-1">
									{{ formatTimestamp(item.uploadedAt) }}
								</td>
								<td class="px-1 text-right">
									<ActionButton
										v-if="item.ccmRecordId === selectedCCMRecord.id"
										icon="mdi-delete"
										tooltip="delete"
										:maxSize="25"
										:minSize="20"
										@clicked="
											deleteId = item.id;
											deleteType = 'ccmAttachment';
											showDeleteDialog = true;
										"
										:busy="deletingAttachmentIds.includes(item.id)" />

									<!-- <ActionButton
										icon="mdi-creation"
										tooltip="summarize-using-ai"
										:maxSize="25"
										:minSize="20"
										@clicked="summarize(item.id)"
										:busy="summarizingAttachmentIds.includes(item.id)" /> -->

									<ActionButton
										icon="mdi-cloud-download"
										tooltip="download-file"
										:maxSize="25"
										:minSize="20"
										@clicked="downloadFile(item.id, item.fileName, 'ccmAttachment')"
										:busy="downloadingAttachmentIds.includes(item.id)" />

									<ActionButton
										icon="mdi-open-in-new"
										tooltip="open-in-new-tab"
										:disabled="excludedFileExtensions.includes(item.fileExtension)"
										disabledTooltip="unsupported-file-type"
										:maxSize="25"
										:minSize="20"
										@clicked="openFileInNewTab(item.id, 'ccmAttachment')"
										:busy="openingConsentFormIds.includes(item.id)" />
								</td>
							</tr>
						</template>
						<template #[`footer.prepend`]>
							<v-row dense class="ma-0">
								<v-col class="text-left">
									<p class="ma-0">
										<v-tooltip top>
											<template #activator="{ on }">
												<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
											</template>
											{{ $t('need-to-select-the-correct-record') }}
										</v-tooltip>
									</p>
								</v-col>
							</v-row>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>

		<!-- Consent Forms -->
		<v-col cols="12" class="pa-1" style="height: 40%">
			<v-card style="height: 100%; min-height: 300px" :loading="dataLoading">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('ccm-consent-forms-header') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<v-icon
								color="primary"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
								@click="synchronizeAddDrawers('ccmConsentFormAdd')">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-data-table
						:items="consentForms"
						:headers="consentFormHeaders"
						:items-per-page="-1"
						style="overflow-y: auto; height: 100%"
						:height="'calc(100% - 59px)'"
						fixed-header
						dense
						sort-by="timestamp"
						sort-desc
						class="data-table-outline"
						:footer-props="{ 'items-per-page-text': '' }">
						<template #item="{ item, index }">
							<tr :class="getRowsForDataTable(index)" @click.stop="openViewDialog(item, 'ccmConsentForm')">
								<td class="text-left px-1">
									{{ item.consentType }}
								</td>
								<td class="text-left px-1 truncate-text" style="max-width: 20vw">
									<v-tooltip bottom>
										<template #activator="{ on }">
											<v-icon v-on="on" :size="$vuetify.breakpoint.mdAndDown ? 19 : 21" color="black">
												{{ getFileIcon(item.fileName) }}
											</v-icon>
										</template>
										{{ item.fileExtension.toUpperCase() }}
									</v-tooltip>
									{{ item.fileName }}
								</td>
								<td class="text-left px-1">
									{{ item.obtainedBy }}
								</td>
								<td class="text-left px-1">
									{{ formatTimestamp(item.recordedAt) }}
								</td>
								<td class="px-1 text-right">
									<ActionButton
										v-if="item.ccmRecordId === selectedCCMRecord.id"
										icon="mdi-delete"
										tooltip="delete"
										:maxSize="25"
										:minSize="20"
										@clicked="
											deleteId = item.id;
											deleteType = 'ccmConsentForm';
											showDeleteDialog = true;
										"
										:busy="deletingConsentFormIds.includes(item.id)" />

									<ActionButton
										icon="mdi-cloud-download"
										tooltip="download-file"
										:maxSize="25"
										:minSize="20"
										@clicked="downloadFile(item.id, item.fileName, 'ccmConsentForm')"
										:busy="downloadingConsentFormIds.includes(item.id)" />

									<ActionButton
										icon="mdi-open-in-new"
										tooltip="open-in-new-tab"
										:disabled="excludedFileExtensions.includes(item.fileExtension)"
										disabledTooltip="unsupported-file-type"
										:maxSize="25"
										:minSize="20"
										@clicked="openFileInNewTab(item.id, 'ccmConsentForm')"
										:busy="openingConsentFormIds.includes(item.id)" />
								</td>
							</tr>
						</template>
						<template #[`footer.prepend`]>
							<v-row dense class="ma-0">
								<v-col class="text-left">
									<p class="ma-0">
										<v-tooltip top>
											<template #activator="{ on }">
												<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
											</template>
											{{ $t('need-to-select-the-correct-record') }}
										</v-tooltip>
									</p>
								</v-col>
							</v-row>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>

		<CCMAttachmentViewDialog
			:showDialog.sync="showAttachmentViewDialog"
			v-if="showAttachmentViewDialog && selectedDocument"
			:selectedAttachment="selectedDocument"
			:excludedFileExtensions="excludedFileExtensions"
			@closeDialog="showAttachmentViewDialog = false"
			@openFileInNewTab="openFileInNewTab"
			@downloadAttachment="downloadFile" />

		<CCMConsentFormViewDialog
			:showDialog.sync="showConsentFormViewDialog"
			v-if="showConsentFormViewDialog && selectedDocument"
			:selectedConsentForm="selectedDocument"
			:excludedFileExtensions="excludedFileExtensions"
			@closeDialog="showConsentFormViewDialog = false"
			@openFileInNewTab="openFileInNewTab"
			@downloadConsentForm="downloadFile" />

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deleteType === 'ccmAttachment' ? deletingAttachmentIds.push(deleteId) : deletingConsentFormIds.push(deleteId);
				deleteFile();
				deleteId = -1;
				deleteType = '';
			" />
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { getRowsForDataTable, formatTimestamp, formatDuration, shortenText } from '@/utils/utilities';

import CCMAttachmentViewDialog from '../../../dialogs/CCMAttachmentViewDialog.vue';
import CCMConsentFormViewDialog from '../../../dialogs/CCMConsentFormViewDialog.vue';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMAttachments',
	components: {
		CCMAttachmentViewDialog,
		CCMConsentFormViewDialog,
		DeleteDialog,
		UserTimeTracker,
		ActionButton,
	},

	data () {
		return {
			key: 0,
			downloadStats: [],
			dataLoading: true,
			deleteId: -1,
			deletingAttachmentIds: [],
			deletingConsentFormIds: [],
			summarizingAttachmentIds: [],
			deleteType: '',
			excludedFileExtensions: [
				'doc',
				'docx',
				'ppt',
				'pptx',
				'xlsx',
				'xls',
				'7z',
				'rar',
				'tar',
				'zip',
				'ods',
				'odp',
				'odt',
			],
			selectedDocument: null,
			showDeleteDialog: false,
			showAttachmentViewDialog: false,
			showConsentFormViewDialog: false,
			downloadingAttachmentIds: [],
			downloadingConsentFormIds: [],
			openingAttachmentIds: [],
			openingConsentFormIds: [],
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			attachments: (state) => state.ccmAttachments.ccmAttachmentByPatientId,
			aiSummaryForCCMAttachment: (state) => state.ccmAttachments.aiSummaryForCCMAttachment,
			consentForms: (state) => state.ccmConsentForms.ccmConsentFormByPatientId,
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		attachmentsHeaders () {
			return [
				{
					text: this.$t('type'),
					value: 'documentType',
					class: 'px-1',
				},
				{
					text: this.$t('file-name-header'),
					value: 'fileName',
					class: 'px-1',
				},
				{
					text: this.$t('uploaded-by'),
					value: 'uploadedBy',
					class: 'px-1',
				},
				{
					text: this.$t('date'),
					value: 'uploadedAt',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},

		consentFormHeaders () {
			return [
				{
					text: this.$t('type'),
					value: 'consentType',
					class: 'px-1',
				},
				{
					text: this.$t('file-name-header'),
					value: 'fileName',
					class: 'px-1',
				},
				{
					text: this.$t('obtained-by'),
					value: 'obtainedBy',
					class: 'px-1',
				},
				{
					text: this.$t('date'),
					value: 'recordedAt',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},
	},

	methods: {
		...mapActions({
			getAttachmentsByPatientId: 'ccmAttachments/getAllCCMAttachmentByPatientId',
			getCCMAttachment: 'ccmAttachments/getCCMAttachment',
			sendAttachmentToAi: 'ccmAttachments/sendAttachmentToAi',
			getConsentFormsByPatientId: 'ccmConsentForms/getAllCCMConsentFormByPatientId',
			getCCMConsentForm: 'ccmConsentForms/getCCMConsentForm',
			deleteAttachment: 'ccmAttachments/deleteCCMAttachment',
			deleteConsentForm: 'ccmConsentForms/deleteCCMConsentForm',
		}),

		getRowsForDataTable,
		formatTimestamp,
		shortenText,
		formatDuration,

		getFileIcon (fileName) {
			const nameArray = fileName.toString().split('.');
			const fileType = nameArray[nameArray.length - 1];

			const fileTypeMap = {
				pdf: 'mdi-file-powerpoint',
				jpeg: 'mdi-file-image',
				jpg: 'mdi-file-image',
				png: 'mdi-file-image',
				txt: 'mdi-file-document',
				doc: 'mdi-file-document',
				docx: 'mdi-file-document',
				xls: 'mdi-file-table',
				xlsx: 'mdi-file-table',
				ppt: 'mdi-file-chart',
				pptx: 'mdi-file-chart',
				csv: 'mdi-file-delimited',
				zip: 'mdi-zip-box',
				'7z': 'mdi-zip-box',
				rar: 'mdi-zip-box',

				// Add more mappings as needed
			};
			return fileTypeMap[fileType] || 'mdi-file';
		},

		async downloadFile (id, fileName, api) {
			let file = null;
			if (api === 'ccmAttachment') {
				this.downloadingAttachmentIds.push(id);
				file = await this.getCCMAttachment(id);
			} else if (api === 'ccmConsentForm') {
				this.downloadingConsentFormIds.push(id);
				file = await this.getCCMConsentForm(id);
			}
			// await new Promise((resolve) => setTimeout(resolve, 10000));
			if (file) {
				const url = window.URL.createObjectURL(new Blob([file]));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', fileName);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			}
			if (api === 'ccmAttachment') {
				this.downloadingAttachmentIds = this.downloadingAttachmentIds.filter((item) => item !== id);
			} else {
				this.downloadingConsentFormIds = this.downloadingConsentFormIds.filter((item) => item !== id);
			}
		},

		async deleteFile () {
			if (this.deleteType === 'ccmAttachment') {
				this.deletingAttachmentIds.push(this.deleteId);
				await this.deleteAttachment(this.deleteId);
				this.deletingAttachmentIds = this.deletingAttachmentIds.filter((item) => item !== this.deleteId);
			} else if (this.deleteType === 'ccmConsentForm') {
				this.deletingConsentFormIds.push(this.deleteId);
				await this.deleteConsentForm(this.deleteId);
				this.deletingConsentFormIds = this.deletingConsentFormIds.filter((item) => item !== this.deleteId);
			}
		},

		async openEditDrawer (carePlan) {
			if (carePlan.ccmRecordId !== 0) {
				await this.getSelectedCarePlan(carePlan.id);
				this.synchronizeEditDrawers('ccmAttachmentEdit');
			}
		},
		async openFileInNewTab (id, api) {
			try {
				let file = null;
				if (api === 'ccmAttachment') {
					this.openingAttachmentIds.push(id);
					file = await this.getCCMAttachment(id);
				} else if (api === 'ccmConsentForm') {
					this.openingConsentFormIds.push(id);
					file = await this.getCCMConsentForm(id);
				}
				const url = window.URL.createObjectURL(new Blob([file], { type: file.type }));
				const link = document.createElement('a');
				link.href = url;
				window.open(link, '_blank');
				// eslint-disable-next-line no-empty
			} catch (error) {
			} finally {
				if (api === 'ccmAttachment') {
					this.openingAttachmentIds = this.openingAttachmentIds.filter((item) => item !== id);
				} else {
					this.openingConsentFormIds = this.openingConsentFormIds.filter((item) => item !== id);
				}
			}
		},
		openViewDialog (file, type) {
			this.selectedDocument = file;

			if (type === 'ccmAttachment') {
				this.showAttachmentViewDialog = true;
			} else if (type === 'ccmConsentForm') {
				this.showConsentFormViewDialog = true;
			}
		},
		async summarize (fileId) {
			this.summarizingAttachmentIds.push(fileId);
			await this.sendAttachmentToAi(fileId);
			// console.log(this.aiSummaryForCCMAttachment);
			this.summarizingAttachmentIds = this.summarizingAttachmentIds.filter((item) => item !== fileId);
		},
		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
	},

	watch: {
		selectedCCMRecord () {
			this.key = !this.key;
		},
	},

	async mounted () {
		await this.getAttachmentsByPatientId(this.detailedUser.userData.id);
		await this.getConsentFormsByPatientId(this.detailedUser.userData.id);
		this.dataLoading = false;
	},
};
</script>
